import React, { useState } from 'react';
import {
  Home,
  Package,
  QrCode,
  Wrench,
  CreditCard,
  Calendar,
  Bell,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  Upload,
  Share2,
  Send,
  PhoneCall,
  MapPin,
  Sparkles,
  Info,
  ChevronRight,
  User,
  Shield,
  Search,
  Receipt,
  Printer,
  Download,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  MaintenanceCategory,
  MaintenancePriority,
  VisitorPass,
  CommonFeeBill,
} from '../types';
import { ReceiptModal } from './ReceiptModal';

export const ResidentDashboard: React.FC = () => {
  const {
    currentUser,
    announcements,
    parcels,
    maintenanceTickets,
    bills,
    visitorPasses,
    facilities,
    bookings,
    createMaintenanceTicket,
    createVisitorPass,
    payBill,
    bookFacility,
    cancelBooking,
  } = useApp();

  const [activeTab, setActiveTab] = useState<'overview' | 'parcels' | 'visitor' | 'maintenance' | 'bills' | 'facilities'>('overview');
  const [selectedBillForReceipt, setSelectedBillForReceipt] = useState<CommonFeeBill | null>(null);

  // Filter data for this resident
  const houseNum = currentUser?.house_number || '88/12';
  const myParcels = parcels.filter((p) => p.house_number === houseNum);
  const waitingParcels = myParcels.filter((p) => p.status === 'waiting');
  
  const myTickets = maintenanceTickets.filter((t) => t.house_number === houseNum);
  const activeTickets = myTickets.filter((t) => t.status !== 'completed' && t.status !== 'cancelled');
  
  const myBills = bills.filter((b) => b.house_number === houseNum);
  const unpaidBills = myBills.filter((b) => b.status === 'unpaid' || b.status === 'overdue');
  
  const myVisitorPasses = visitorPasses.filter((v) => v.host_house_number === houseNum);
  const myBookings = bookings.filter((b) => b.house_number === houseNum && b.status === 'confirmed');

  // New Maintenance Form State
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketCategory, setTicketCategory] = useState<MaintenanceCategory>('electricity');
  const [ticketPriority, setTicketPriority] = useState<MaintenancePriority>('medium');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketImageUrl, setTicketImageUrl] = useState('');
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);

  // New Visitor Pass State
  const [showNewVisitorModal, setShowNewVisitorModal] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [visitorPlate, setVisitorPlate] = useState('');
  const [visitorPurpose, setVisitorPurpose] = useState('พบปะสังสรรค์ / เยี่ยมเยียน');
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [createdPass, setCreatedPass] = useState<VisitorPass | null>(null);

  // Pay Bill State
  const [payingBillId, setPayingBillId] = useState<string | null>(null);
  const [slipUrl, setSlipUrl] = useState('');
  const [isPaying, setIsPaying] = useState(false);

  // Facility Booking State
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(facilities[0]?.id || 'fac-1');
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [timeSlot, setTimeSlot] = useState('17:00 - 18:30 น.');
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Submit Ticket
  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketTitle || !ticketDescription) return;
    setIsSubmittingTicket(true);
    await createMaintenanceTicket({
      house_number: houseNum,
      reporter_name: currentUser?.full_name || 'ลูกบ้าน',
      title: ticketTitle,
      category: ticketCategory,
      priority: ticketPriority,
      description: ticketDescription,
      image_url: ticketImageUrl || undefined,
    });
    setIsSubmittingTicket(false);
    setShowNewTicketModal(false);
    setTicketTitle('');
    setTicketDescription('');
    setTicketImageUrl('');
  };

  // Submit Visitor Pass
  const handleVisitorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitorName || !visitorPlate) return;
    const pass = await createVisitorPass({
      host_house_number: houseNum,
      host_name: currentUser?.full_name || 'ลูกบ้าน',
      visitor_name: visitorName,
      license_plate: visitorPlate,
      visit_date: visitDate,
      purpose: visitorPurpose,
    });
    setCreatedPass(pass);
    setVisitorName('');
    setVisitorPlate('');
  };

  // Submit Bill Payment
  const handlePaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payingBillId) return;
    setIsPaying(true);
    await payBill(
      payingBillId,
      slipUrl || 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=500&auto=format&fit=crop&q=80'
    );
    setIsPaying(false);
    setPayingBillId(null);
    setSlipUrl('');
  };

  // Facility Book
  const handleFacilityBook = async (e: React.FormEvent) => {
    e.preventDefault();
    const facility = facilities.find((f) => f.id === selectedFacilityId);
    if (!facility) return;
    await bookFacility({
      facility_id: facility.id,
      facility_name: facility.name,
      house_number: houseNum,
      resident_name: currentUser?.full_name || 'ลูกบ้าน',
      booking_date: bookingDate,
      time_slot: timeSlot,
    });
    setBookingSuccess(true);
    setTimeout(() => setBookingSuccess(false), 3000);
  };

  return (
    <div id="resident-dashboard-wrapper" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-blue-100 text-xs font-medium backdrop-blur-xs mb-2">
              <Home className="w-3.5 h-3.5" />
              <span>บ้านเลขที่ {houseNum} • โครงการนราวดี อิลิแกนซ์</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold">
              สวัสดี, {currentUser?.full_name || 'คุณลูกบ้าน'}
            </h2>
            <p className="text-blue-100 text-xs sm:text-sm mt-1">
              ยินดีต้อนรับสู่พอร์ทัลบริการลูกบ้าน ตรวจสอบพัสดุ ออกบัตรผ่านแขก และแจ้งซ่อมบำรุงได้สะดวกทุกที่
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                setActiveTab('visitor');
                setShowNewVisitorModal(true);
              }}
              className="px-4 py-2 bg-white text-blue-800 hover:bg-blue-50 text-xs font-semibold rounded-xl shadow-sm transition-colors flex items-center gap-1.5"
            >
              <QrCode className="w-4 h-4 text-blue-700" />
              สร้างบัตรผ่านแขก (E-Pass)
            </button>
            <button
              onClick={() => {
                setActiveTab('maintenance');
                setShowNewTicketModal(true);
              }}
              className="px-4 py-2 bg-blue-600/80 hover:bg-blue-600 border border-white/20 text-white text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5"
            >
              <Wrench className="w-4 h-4" />
              แจ้งซ่อมบำรุง
            </button>
          </div>
        </div>

        {/* Subtle Decorative Pattern */}
        <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
      </div>

      {/* Metric Quick Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Parcels */}
        <div
          onClick={() => setActiveTab('parcels')}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            activeTab === 'parcels' ? 'bg-amber-50/80 border-amber-300 ring-2 ring-amber-400/40' : 'bg-white border-slate-200 hover:border-amber-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">พัสดุรอรับที่ป้อม</span>
            <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
              <Package className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">{waitingParcels.length}</span>
            <span className="text-xs text-amber-600 font-medium">รายการ</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {waitingParcels.length > 0 ? 'มีพัสดุมาถึงป้อม รปภ. แล้ว' : 'ไม่มีพัสดุตกค้าง'}
          </p>
        </div>

        {/* Maintenance */}
        <div
          onClick={() => setActiveTab('maintenance')}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            activeTab === 'maintenance' ? 'bg-blue-50/80 border-blue-300 ring-2 ring-blue-400/40' : 'bg-white border-slate-200 hover:border-blue-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">งานแจ้งซ่อมค้าง</span>
            <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
              <Wrench className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">{activeTickets.length}</span>
            <span className="text-xs text-blue-600 font-medium">เรื่อง</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {activeTickets.length > 0 ? 'ฝ่ายช่างกำลังดำเนินการ' : 'ทุกเรื่องดำเนินการเสร็จสิ้น'}
          </p>
        </div>

        {/* Bills */}
        <div
          onClick={() => setActiveTab('bills')}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            activeTab === 'bills' ? 'bg-rose-50/80 border-rose-300 ring-2 ring-rose-400/40' : 'bg-white border-slate-200 hover:border-rose-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">ค่าส่วนกลางค้างชำระ</span>
            <div className="p-2 rounded-lg bg-rose-100 text-rose-700">
              <CreditCard className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">{unpaidBills.length}</span>
            <span className="text-xs text-rose-600 font-medium">งวด</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {unpaidBills.length > 0 ? `ยอดรวม ฿${unpaidBills.reduce((acc, b) => acc + b.amount, 0).toLocaleString()}` : 'ชำระครบถ้วนแล้ว'}
          </p>
        </div>

        {/* Visitor Passes */}
        <div
          onClick={() => setActiveTab('visitor')}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            activeTab === 'visitor' ? 'bg-emerald-50/80 border-emerald-300 ring-2 ring-emerald-400/40' : 'bg-white border-slate-200 hover:border-emerald-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">บัตรผ่านแขกวันนี้</span>
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
              <QrCode className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-800">{myVisitorPasses.length}</span>
            <span className="text-xs text-emerald-600 font-medium">รายการ</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">สร้าง E-Pass ให้เข้าป้อม รปภ. ได้รวดเร็ว</p>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="flex border-b border-slate-200 overflow-x-auto gap-2 text-xs sm:text-sm font-medium">
        <button
          onClick={() => setActiveTab('overview')}
          className={`pb-3 px-3.5 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'overview' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Home className="w-4 h-4" />
          ภาพรวม & ข่าวสาร
        </button>

        <button
          onClick={() => setActiveTab('parcels')}
          className={`pb-3 px-3.5 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 relative ${
            activeTab === 'parcels' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Package className="w-4 h-4" />
          พัสดุและไปรษณีย์
          {waitingParcels.length > 0 && (
            <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold">
              {waitingParcels.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('visitor')}
          className={`pb-3 px-3.5 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'visitor' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <QrCode className="w-4 h-4" />
          บัตรผ่านผู้มาติดต่อ (E-Pass)
        </button>

        <button
          onClick={() => setActiveTab('maintenance')}
          className={`pb-3 px-3.5 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'maintenance' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Wrench className="w-4 h-4" />
          แจ้งซ่อมและร้องเรียน
        </button>

        <button
          onClick={() => setActiveTab('bills')}
          className={`pb-3 px-3.5 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'bills' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          ค่าส่วนกลาง
          {unpaidBills.length > 0 && (
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
          )}
        </button>

        <button
          onClick={() => setActiveTab('facilities')}
          className={`pb-3 px-3.5 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'facilities' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Calendar className="w-4 h-4" />
          จองสิ่งอำนวยความสะดวก
        </button>
      </div>

      {/* TAB CONTENT */}

      {/* 1. OVERVIEW & ANNOUNCEMENTS */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Announcements Feed (Left 2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-600" />
                ข่าวสารและประกาศจากนิติบุคคล
              </h3>
              <span className="text-xs text-slate-500">อัปเดตล่าสุดวันนี้</span>
            </div>

            <div className="space-y-3">
              {announcements.map((ann) => (
                <div
                  key={ann.id}
                  className={`p-5 rounded-2xl bg-white border transition-all ${
                    ann.is_pinned ? 'border-blue-300 shadow-sm bg-gradient-to-br from-blue-50/30 to-white' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {ann.is_pinned && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-600 text-white">
                            ปักหมุด
                          </span>
                        )}
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            ann.category === 'urgent'
                              ? 'bg-rose-100 text-rose-700'
                              : ann.category === 'maintenance'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {ann.category === 'urgent'
                            ? 'ด่วนมาก'
                            : ann.category === 'maintenance'
                            ? 'งานซ่อมบำรุง'
                            : 'ทั่วไป'}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(ann.created_at).toLocaleDateString('th-TH', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                      <h4 className="font-semibold text-slate-800 text-sm sm:text-base mt-1">{ann.title}</h4>
                    </div>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed whitespace-pre-line">
                    {ann.content}
                  </p>

                  {ann.image_url && (
                    <div className="mt-3 rounded-xl overflow-hidden max-h-56 border border-slate-100">
                      <img src={ann.image_url} alt="ข่าวสาร" className="w-full h-full object-cover" />
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                    <span>ประกาศโดย: {ann.author_name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Sidebar: Emergency Contacts & Quick Status */}
          <div className="space-y-4">
            {/* Contacts */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-3">
              <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <PhoneCall className="w-4 h-4 text-blue-600" />
                เบอร์ติดต่อฉุกเฉินและบริการ
              </h4>

              <div className="space-y-2.5 text-xs">
                <div className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">ป้อม รปภ. หลัก (24 ชม.)</p>
                    <p className="text-slate-500 text-[11px]">ประตูทางเข้าโครงการ</p>
                  </div>
                  <a
                    href="tel:028881234"
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center gap-1"
                  >
                    โทร 02-888-1234
                  </a>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">สำนักงานนิติบุคคล</p>
                    <p className="text-slate-500 text-[11px]">เปิด 08.30 - 18.00 น.</p>
                  </div>
                  <a
                    href="tel:028889999"
                    className="px-3 py-1.5 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-900 flex items-center gap-1"
                  >
                    โทร 02-888-9999
                  </a>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">ฝ่ายช่างฉุกเฉิน</p>
                    <p className="text-slate-500 text-[11px]">ไฟดับ ท่อแตก ปัญหาส่วนกลาง</p>
                  </div>
                  <a
                    href="tel:0898887777"
                    className="px-3 py-1.5 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-900 flex items-center gap-1"
                  >
                    โทร 089-888-7777
                  </a>
                </div>
              </div>
            </div>

            {/* My Active Bookings */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-3">
              <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-600" />
                การจองพื้นที่ส่วนกลางของฉัน
              </h4>
              {myBookings.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">ยังไม่มีการจองพื้นที่ส่วนกลาง</p>
              ) : (
                <div className="space-y-2">
                  {myBookings.map((b) => (
                    <div key={b.id} className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl text-xs space-y-1">
                      <p className="font-semibold text-indigo-950">{b.facility_name}</p>
                      <p className="text-indigo-700 text-[11px]">
                        วันที่ {b.booking_date} • เวลา {b.time_slot}
                      </p>
                      <button
                        onClick={() => cancelBooking(b.id)}
                        className="text-[10px] text-rose-600 hover:underline pt-1 font-medium block"
                      >
                        ยกเลิกการจอง
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. PARCELS (พัสดุและไปรษณีย์) */}
      {activeTab === 'parcels' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">พัสดุและไปรษณีย์ภัณฑ์</h3>
              <p className="text-xs text-slate-500">สำหรับบ้านเลขที่ {houseNum}</p>
            </div>
          </div>

          {myParcels.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 mx-auto flex items-center justify-center">
                <Package className="w-6 h-6" />
              </div>
              <h4 className="font-semibold text-slate-700 text-base">ไม่มีรายการพัสดุในขณะนี้</h4>
              <p className="text-xs text-slate-400 max-w-sm mx-auto">
                เมื่อมีพัสดุมาส่งที่ป้อม รปภ. หรือนิติบุคคล เจ้าหน้าที่จะลงทะเบียนและระบบจะแจ้งเตือนทันที
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myParcels.map((parcel) => (
                <div
                  key={parcel.id}
                  className={`bg-white rounded-2xl border p-5 space-y-3 relative transition-all ${
                    parcel.status === 'waiting'
                      ? 'border-amber-300 shadow-sm bg-gradient-to-b from-amber-50/20 to-white'
                      : 'border-slate-200 opacity-80'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {parcel.courier_name}
                      </span>
                      <p className="font-mono text-sm font-bold text-slate-900 mt-1">
                        {parcel.tracking_number}
                      </p>
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        parcel.status === 'waiting'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {parcel.status === 'waiting' ? '📦 รอรับที่ป้อม' : '✅ รับแล้ว'}
                    </span>
                  </div>

                  {parcel.image_url && (
                    <div className="rounded-xl overflow-hidden h-36 border border-slate-200">
                      <img src={parcel.image_url} alt="พัสดุ" className="w-full h-full object-cover" />
                    </div>
                  )}

                  <div className="text-xs text-slate-600 space-y-1 pt-1">
                    <p className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span>
                        มาถึงเมื่อ: {new Date(parcel.received_at).toLocaleDateString('th-TH', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })} น.
                      </span>
                    </p>
                    {parcel.note && <p className="text-slate-500 italic text-[11px] bg-slate-50 p-2 rounded-lg">{parcel.note}</p>}
                  </div>

                  {parcel.status === 'waiting' && (
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                      <span className="text-slate-400 text-[11px]">นำบัตรประชาชนหรือแจ้งบ้านเลขที่เพื่อรับ</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 3. VISITOR PASS / E-PASS */}
      {activeTab === 'visitor' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">บัตรผ่านผู้มาติดต่อ (E-Pass / QR Pass)</h3>
              <p className="text-xs text-slate-500">สร้าง QR Pass ล่วงหน้า เพื่อให้แขก/ช่าง/ไรเดอร์ ผ่านเข้าป้อม รปภ. ได้สะดวกรวดเร็ว</p>
            </div>
            <button
              onClick={() => setShowNewVisitorModal(true)}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors flex items-center gap-1.5 self-start sm:self-auto"
            >
              <Plus className="w-4 h-4" />
              สร้างบัตรผ่านแขกใหม่
            </button>
          </div>

          {/* New Created Pass Preview Popup */}
          {createdPass && (
            <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-700 text-white rounded-2xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 animate-fadeIn">
              <div className="space-y-2 text-center md:text-left">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-emerald-100 text-xs font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>สร้างบัตรผ่านสำเร็จแล้ว</span>
                </div>
                <h4 className="text-xl font-bold">บัตรผ่าน: {createdPass.visitor_name}</h4>
                <p className="text-xs text-emerald-100">
                  ทะเบียน: <span className="font-mono font-bold bg-white/20 px-2 py-0.5 rounded">{createdPass.license_plate}</span> • วันที่เข้าพบ: {createdPass.visit_date}
                </p>
                <p className="text-xs text-emerald-200">ส่งรหัสนี้ให้ผู้มาติดต่อแสดงที่ป้อม รปภ.</p>
              </div>

              <div className="bg-white p-4 rounded-xl text-slate-900 text-center shadow-lg w-48">
                <div className="w-24 h-24 mx-auto bg-slate-900 text-white rounded-lg flex flex-col items-center justify-center p-2">
                  <QrCode className="w-14 h-14" />
                  <span className="text-[9px] font-mono tracking-widest mt-1">E-PASS</span>
                </div>
                <div className="mt-2">
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Pass Code</span>
                  <div className="text-xl font-bold font-mono tracking-wider text-blue-700">
                    {createdPass.pass_code}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Visitor Pass History Table */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
            <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-700">
              ประวัติบัตรผ่านสำหรับบ้านเลขที่ {houseNum}
            </div>
            {myVisitorPasses.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">ยังไม่มีประวัติการสร้างบัตรผ่านแขก</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50/50 text-slate-500 font-medium border-b border-slate-200">
                    <tr>
                      <th className="p-3.5">รหัสผ่าน (Pass Code)</th>
                      <th className="p-3.5">ผู้มาติดต่อ</th>
                      <th className="p-3.5">ทะเบียนรถ</th>
                      <th className="p-3.5">จุดประสงค์</th>
                      <th className="p-3.5">วันที่นัดหมาย</th>
                      <th className="p-3.5">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {myVisitorPasses.map((v) => (
                      <tr key={v.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="p-3.5 font-mono font-bold text-blue-700">{v.pass_code}</td>
                        <td className="p-3.5 font-medium text-slate-800">{v.visitor_name}</td>
                        <td className="p-3.5 font-mono text-slate-600">{v.license_plate}</td>
                        <td className="p-3.5 text-slate-600">{v.purpose}</td>
                        <td className="p-3.5 text-slate-500">{v.visit_date}</td>
                        <td className="p-3.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              v.status === 'checked_in'
                                ? 'bg-emerald-100 text-emerald-800'
                                : v.status === 'checked_out'
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {v.status === 'approved' && 'อนุมัติแล้ว (รอเข้า)'}
                            {v.status === 'checked_in' && `เข้าหมู่บ้านแล้ว (${v.entry_time || ''})`}
                            {v.status === 'checked_out' && `ออกจากหมู่บ้านแล้ว (${v.exit_time || ''})`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 4. MAINTENANCE (แจ้งซ่อมและร้องเรียน) */}
      {activeTab === 'maintenance' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">แจ้งซ่อมและข้อร้องเรียน</h3>
              <p className="text-xs text-slate-500">แจ้งปัญหาไฟฟ้า น้ำประปา สวนสาธารณะ หรือระบบส่วนกลาง</p>
            </div>
            <button
              onClick={() => setShowNewTicketModal(true)}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors flex items-center gap-1.5 self-start sm:self-auto"
            >
              <Plus className="w-4 h-4" />
              สร้างรายการแจ้งซ่อมใหม่
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {myTickets.map((t) => (
              <div key={t.id} className="bg-white p-5 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                        t.priority === 'urgent' || t.priority === 'high'
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      ความเร่งด่วน: {t.priority === 'urgent' ? 'ด่วนที่สุด' : t.priority === 'high' ? 'สูง' : 'ปกติ'}
                    </span>
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base mt-1.5">{t.title}</h4>
                  </div>

                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${
                      t.status === 'completed'
                        ? 'bg-emerald-100 text-emerald-800'
                        : t.status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {t.status === 'pending' && '⏳ รอดำเนินการ'}
                    {t.status === 'in_progress' && '🔧 กำลังดำเนินการ'}
                    {t.status === 'completed' && '✅ เสร็จสิ้นแล้ว'}
                    {t.status === 'cancelled' && 'ยกเลิก'}
                  </span>
                </div>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{t.description}</p>

                {t.image_url && (
                  <div className="rounded-xl overflow-hidden max-h-48 border border-slate-200">
                    <img src={t.image_url} alt="ภาพแจ้งซ่อม" className="w-full h-full object-cover" />
                  </div>
                )}

                {t.staff_notes && (
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-900 space-y-1">
                    <p className="font-semibold flex items-center gap-1 text-blue-800">
                      <Sparkles className="w-3 h-3" />
                      ความคืบหน้าจากฝ่ายช่าง ({t.assigned_to || 'ทีมช่าง'}):
                    </p>
                    <p className="text-blue-700">{t.staff_notes}</p>
                  </div>
                )}

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                  <span>แจ้งเมื่อ: {new Date(t.created_at).toLocaleDateString('th-TH')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. COMMON FEES & BILLS */}
      {activeTab === 'bills' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">ค่าส่วนกลางและใบแจ้งหนี้</h3>
              <p className="text-xs text-slate-500">ตรวจสอบรอบบิล ชำระเงินผ่านระบบ PromptPay พร้อมแนบสลิป</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Bills List */}
            <div className="lg:col-span-2 space-y-3">
              {myBills.map((bill) => (
                <div
                  key={bill.id}
                  className="bg-white p-5 rounded-2xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-base text-slate-900">รอบบิล {bill.period}</span>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          bill.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-800'
                            : bill.status === 'pending_verification'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}
                      >
                        {bill.status === 'paid' && 'ชำระแล้ว'}
                        {bill.status === 'pending_verification' && 'รอตรวจสอบสลิป'}
                        {bill.status === 'unpaid' && 'ยังไม่ได้ชำระ'}
                        {bill.status === 'overdue' && 'เกินกำหนดชำระ'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">กำหนดชำระภายใน: {bill.due_date}</p>
                    {bill.verified_by && (
                      <p className="text-[11px] text-emerald-600">อนุมัติโดย: {bill.verified_by}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-700">฿{bill.amount.toLocaleString()}</div>
                      <span className="text-[10px] text-slate-400">บาท / เดือน</span>
                    </div>

                    {bill.status === 'unpaid' || bill.status === 'overdue' ? (
                      <button
                        onClick={() => setPayingBillId(bill.id)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors shrink-0 cursor-pointer"
                      >
                        ชำระเงิน
                      </button>
                    ) : bill.status === 'paid' ? (
                      <button
                        onClick={() => setSelectedBillForReceipt(bill)}
                        className="px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5 shrink-0 cursor-pointer shadow-2xs"
                      >
                        <Receipt className="w-4 h-4 text-emerald-600" />
                        <span>ใบเสร็จรับเงิน (PDF)</span>
                      </button>
                    ) : (
                      <span className="text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                        รอนิติตรวจสอบสลิป
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* PromptPay QR & Account Info Box */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-4">
              <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-600" />
                ช่องทางชำระเงินค่าส่วนกลาง
              </h4>

              <div className="bg-slate-900 text-white p-4 rounded-xl text-center space-y-2">
                <p className="text-xs text-slate-300">PromptPay QR Code นิติบุคคล</p>
                <div className="bg-white p-3 rounded-lg w-40 h-40 mx-auto flex items-center justify-center">
                  <QrCode className="w-32 h-32 text-slate-900" />
                </div>
                <p className="text-xs font-mono text-emerald-400">0-8888-99999-00-1</p>
                <p className="text-[11px] text-slate-400">นิติบุคคลหมู่บ้านจัดสรร นราวดี อิลิแกนซ์</p>
              </div>

              <div className="text-xs text-slate-600 space-y-1 bg-slate-50 p-3 rounded-xl">
                <p className="font-semibold text-slate-800">ธนาคารกสิกรไทย (KBANK)</p>
                <p>เลขที่บัญชี: <span className="font-mono font-bold text-blue-700">123-4-56789-0</span></p>
                <p className="text-[11px] text-slate-500">โอนแล้วกรุณาแนบสลิปในระบบเพื่อให้นิติบุคคลออกใบเสร็จ</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. FACILITY BOOKINGS (จองสิ่งอำนวยความสะดวก) */}
      {activeTab === 'facilities' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-bold text-slate-900 text-lg">สิ่งอำนวยความสะดวกและคลับเฮาส์</h3>
            <p className="text-xs text-slate-500">จองสิทธิ์ใช้งานพื้นที่ส่วนกลางล่วงหน้า</p>
          </div>

          {bookingSuccess && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>การจองสำเร็จแล้ว! ข้อมูลจะถูกบันทึกในระบบและประสานงานกับเจ้าหน้าที่คลับเฮาส์</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {facilities.map((fac) => (
              <div key={fac.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs flex flex-col">
                <div className="h-44 relative overflow-hidden">
                  <img src={fac.image_url} alt={fac.name} className="w-full h-full object-cover" />
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-slate-900/80 text-white text-[11px] backdrop-blur-xs font-medium">
                    เปิด {fac.open_time} - {fac.close_time} น.
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-base">{fac.name}</h4>
                    <p className="text-xs text-slate-600 mt-1">{fac.description}</p>
                    {fac.rules && (
                      <p className="text-[11px] text-slate-400 mt-2 bg-slate-50 p-2 rounded-lg">
                        📌 กฎระเบียบ: {fac.rules}
                      </p>
                    )}
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500">ความจุ: {fac.max_capacity} ท่าน/รอบ</span>
                    <button
                      onClick={() => {
                        setSelectedFacilityId(fac.id);
                        document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className={`px-4 py-2 text-xs font-semibold rounded-xl transition-colors ${
                        selectedFacilityId === fac.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      {selectedFacilityId === fac.id ? 'เลือกอยู่นี้' : 'จองใช้งาน'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Booking Form */}
          <div id="booking-section" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <h4 className="font-bold text-slate-900 text-base flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              แบบฟอร์มยืนยันการจองพื้นที่ส่วนกลาง
            </h4>

            <form onSubmit={handleFacilityBook} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">สถานที่</label>
                <select
                  value={selectedFacilityId}
                  onChange={(e) => setSelectedFacilityId(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">วันที่ต้องการใช้งาน</label>
                <input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">ช่วงเวลา</label>
                <select
                  value={timeSlot}
                  onChange={(e) => setTimeSlot(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="07:00 - 08:30 น.">07:00 - 08:30 น.</option>
                  <option value="09:00 - 11:00 น.">09:00 - 11:00 น.</option>
                  <option value="14:00 - 16:00 น.">14:00 - 16:00 น.</option>
                  <option value="17:00 - 18:30 น.">17:00 - 18:30 น.</option>
                  <option value="19:00 - 20:30 น.">19:00 - 20:30 น.</option>
                </select>
              </div>

              <div className="sm:col-span-3 flex justify-end">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  ยืนยันการจองสิทธิ์
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: NEW MAINTENANCE TICKET */}
      {showNewTicketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 bg-blue-700 text-white flex items-center justify-between">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                สร้างรายการแจ้งซ่อม / ร้องเรียนปัญหา
              </h3>
              <button
                onClick={() => setShowNewTicketModal(false)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleTicketSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">หัวข้อปัญหา</label>
                <input
                  type="text"
                  placeholder="เช่น หลอดไฟส่องสว่างดับ, ท่อประปารั่วซึม"
                  value={ticketTitle}
                  onChange={(e) => setTicketTitle(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">หมวดหมู่งาน</label>
                  <select
                    value={ticketCategory}
                    onChange={(e) => setTicketCategory(e.target.value as MaintenanceCategory)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="electricity">ระบบไฟฟ้าและแสงสว่าง</option>
                    <option value="water">ระบบประปาและระบายน้ำ</option>
                    <option value="road">ถนนและทางเท้า</option>
                    <option value="landscape">งานสวนและต้นไม้</option>
                    <option value="security">ความปลอดภัย</option>
                    <option value="other">อื่นๆ</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">ระดับความเร่งด่วน</label>
                  <select
                    value={ticketPriority}
                    onChange={(e) => setTicketPriority(e.target.value as MaintenancePriority)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="low">ปกติ (ภายใน 3-5 วัน)</option>
                    <option value="medium">ปานกลาง (ภายใน 1-2 วัน)</option>
                    <option value="high">สูง (ภายใน 24 ชม.)</option>
                    <option value="urgent">ด่วนที่สุด (ทันที)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">รายละเอียดปัญหาและสถานที่</label>
                <textarea
                  rows={3}
                  placeholder="ระบุจุดที่เกิดปัญหาอย่างละเอียด..."
                  value={ticketDescription}
                  onChange={(e) => setTicketDescription(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">ลิงก์รูปภาพประกอบ (ถ้ามี)</label>
                <input
                  type="text"
                  placeholder="https://example.com/photo.jpg"
                  value={ticketImageUrl}
                  onChange={(e) => setTicketImageUrl(e.target.value)}
                  className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewTicketModal(false)}
                  className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingTicket}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  {isSubmittingTicket ? 'กำลังส่งข้อมูล...' : 'ส่งเรื่องแจ้งซ่อม'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: NEW VISITOR PASS */}
      {showNewVisitorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-emerald-700 text-white flex items-center justify-between">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                สร้างบัตรผ่านผู้มาติดต่อ (E-Pass)
              </h3>
              <button
                onClick={() => setShowNewVisitorModal(false)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={(e) => {
                handleVisitorSubmit(e);
                setShowNewVisitorModal(false);
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">ชื่อผู้มาติดต่อ</label>
                <input
                  type="text"
                  placeholder="เช่น คุณสมชาย (ช่างแอร์) / คุณกิตติ"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">ทะเบียนรถ</label>
                <input
                  type="text"
                  placeholder="เช่น 1กข-9999 กทม."
                  value={visitorPlate}
                  onChange={(e) => setVisitorPlate(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">จุดประสงค์ในการมา</label>
                <select
                  value={visitorPurpose}
                  onChange={(e) => setVisitorPurpose(e.target.value)}
                  className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
                >
                  <option value="พบปะสังสรรค์ / เยี่ยมเยียน">พบปะสังสรรค์ / เยี่ยมเยียน</option>
                  <option value="งานช่าง / ซ่อมแซมบ้าน">งานช่าง / ซ่อมแซมบ้าน</option>
                  <option value="ส่งพัสดุ / เฟอร์นิเจอร์ชิ้นใหญ่">ส่งพัสดุ / เฟอร์นิเจอร์ชิ้นใหญ่</option>
                  <option value="บริการทำความสะอาด / แม่บ้าน">บริการทำความสะอาด / แม่บ้าน</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">วันที่เข้าพบ</label>
                <input
                  type="date"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewVisitorModal(false)}
                  className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  สร้างบัตรผ่าน
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: PAY BILL & UPLOAD SLIP */}
      {payingBillId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-blue-700 text-white flex items-center justify-between">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                ชำระค่าส่วนกลางและแนบสลิป
              </h3>
              <button
                onClick={() => setPayingBillId(null)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePaySubmit} className="p-6 space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl text-center">
                <span className="text-xs text-blue-700">สแกน PromptPay เพื่อโอนเงินยอด</span>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  ฿{myBills.find((b) => b.id === payingBillId)?.amount.toLocaleString() || '1,500'}
                </p>
                <p className="text-[11px] text-blue-600 mt-0.5">PromptPay: 0-8888-99999-00-1</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  URL รูปสลิปการโอนเงิน
                </label>
                <input
                  type="text"
                  placeholder="https://example.com/slip.jpg หรือกดส่งเพื่อใช้สลิปตัวอย่าง"
                  value={slipUrl}
                  onChange={(e) => setSlipUrl(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPayingBillId(null)}
                  className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isPaying}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  {isPaying ? 'กำลังบันทึก...' : 'ยืนยันการส่งสลิป'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RECEIPT MODAL FOR RESIDENT */}
      {selectedBillForReceipt && (
        <ReceiptModal
          bill={selectedBillForReceipt}
          onClose={() => setSelectedBillForReceipt(null)}
        />
      )}
    </div>
  );
};
